import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import IC_AUDIO_FILE from '@/assets/import/ic_file_audio.svg?react';
import IC_TIPS from '@/assets/import/ic_tips.svg?react';
import IC_ADD from '@/assets/import/ic_add_file.svg?react';
import IC_CLOSE from '@/assets/common/ic_collect_close.svg?react';
import IC_CLOSE_ITEM from '@/assets/import/ic_close_item.svg?react';
import { Upload, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { formatBytes } from '@/utils/formatter';
import { IFile } from '@/types/fileImport';
import Tooltip from '@/component/Tooltip';
import fileImportStore from '@/store/fileImport.store';
import { ACCEPT_AUDIO_TYPES } from '@/constants/fileImport';
import * as AudioImportHandler from '@/bussiness/AudioImportHandler';

const { Dragger } = Upload;

interface AudioImportDialogProps {
  onClose: () => void;
}

export default function AudioImportDialog(props: AudioImportDialogProps) {
  const { onClose } = props;
  const [fileList, setFileList] = useState<IFile[]>([]);
  const fileListRef = useRef<IFile[]>([]); // 使用 ref 保存最新的文件列表
  const [canClick, setCanClick] = useState(false);
  const benefit = fileImportStore((store) => store.benefit);
  const benefitUsed = fileImportStore((store) => store.benefitUsed);
  const uploadingCount = fileImportStore((store) => store.uploadingCount);

  // 可上传个数
  const availableCount = Math.max(0, benefit - (benefitUsed + fileList.length));

  // 监听文件列表变化，同步 ref，更新 canClick 状态
  useEffect(() => {
    fileListRef.current = fileList;
    const canStart = AudioImportHandler.checkCanStartAudioImport(fileList);
    setCanClick(canStart);
  }, [fileList]);

  // 删除文件
  const handleDeleteFile = (index: number, uid: string) => {
    AudioImportHandler.deleteAudioFile(uid);
    setFileList(prev => prev.filter((_, i) => i !== index));
  };

  // 开始导入
  const handleStart = () => {
    AudioImportHandler.startAudioImport(fileList);
    onClose();
  };

  // 关闭导入
  const handleClose = () => {
    setFileList([]);
    AudioImportHandler.closeAudioImport();
    onClose();
  };

  // 文件处理回调
  const handleFileProcess = async (file: RcFile, uid: string, filesLength: number) => {
    // 当前已使用的文件数量
    const totalUsedCount = benefitUsed + fileListRef.current.length + filesLength;
    // 验证文件
    if (!AudioImportHandler.validateAudioFile(file, totalUsedCount, benefit)) {
      return false;
    }

    AudioImportHandler.addStopFileRead(file.uid);

    // 创建文件对象并添加到列表
    const fileItem: IFile = {
      file,
      uid,
      inProcess: true,
      duration: 0,
      size: file.size
    };

    setFileList(prev => {
      const existingFile = prev.find(f => f.uid === uid);
      if (existingFile) {
        return prev; // 文件已存在，不重复添加
      }
      return [...prev, fileItem];
    });

    // 处理文件
    await AudioImportHandler.processAudioFile(
      file,
      uid,
      (updatedFile: IFile) => {
        setFileList(prev => {
          const index = prev.findIndex(f => f.uid === uid);
          if (index >= 0) {
            const newList = [...prev];
            newList[index] = updatedFile;
            return newList;
          }
          return prev;
        });
      },
      // 错误回调：从列表中移除文件
      () => {
        setFileList(prev => prev.filter(f => f.uid !== uid));
      }
    );
  };

  // 创建 UploadProps
  const uploadProps: UploadProps = {
    name: 'Maxhub会记',
    accept: ACCEPT_AUDIO_TYPES.join(','),
    multiple: true,
    showUploadList: false,
    onDrop: (event) => {
      const droppedFiles = Array.from(event.dataTransfer.files);
      console.log('droppedFiles', droppedFiles);
      AudioImportHandler.validateDroppedAudioFiles(droppedFiles);
    },
    beforeUpload: (file: RcFile, fileList: RcFile[]) => {
      console.log('fileList.length =', fileList.length);
      handleFileProcess(file, file.uid, fileList.length);
      return false;
    }
  };

  return createPortal(
    <div className='fixed top-0 flex justify-center items-center w-full h-full bg-black bg-opacity-50'>
      <div className={`flex flex-col w-130 h-106 rd-3 pt-3 bg-white select-none`} id='dragBox'>
        <div className='flex items-center mx-6'>
          <span className='text-size-4 text-mx-secondary font-bold'>导入本地音视频文件</span>
          <Tooltip placement='bottom' content={<span className='text-size-3'>
            ·音频格式：mp3/wav/m4a <br />
            单个文件不超过200M<br />
            ·文档大小：单个文件时长不超过3小时，<br />
            每日最多上传10个文件</span>}
          >
            <IC_TIPS className='w-4 h-4 ml-2' />
          </Tooltip>
          <div className='flex-1'></div>
          <IC_CLOSE className='w-6 h-6 cursor-pointer' onClick={handleClose} />
        </div>
        <Dragger {...uploadProps} maxCount={availableCount} className='mt-5 mb-4 mx-6 flex-1' openFileDialogOnClick={fileList.length <= 0}>
          {
            fileList.length > 0 ?
              <div className='h-75 flex flex-col justify-start py-4 pl-6 pr-2 overflow-hidden'>
                <div className='flex justify-between mr-4'>
                  <span className='text-size-3.5 text-#626773 text-align-start'>
                    今日可上传文件数：{benefitUsed + uploadingCount + fileList.length}<span className='text-#A1A7B2'>/{benefit}</span>
                  </span>
                  <Upload {...uploadProps} maxCount={availableCount}>
                    <span className='text-size-3.5 text-#4981F2 cursor-pointer'>添加</span>
                  </Upload>
                </div>
                <div className='overflow-hidden overflow-y-scroll'>
                  {fileList.map((file, index) => {
                    return (
                      <div className='h-17 w-106 flex items-center' key={file.uid}>
                        <IC_AUDIO_FILE className='w-8 h-8 mr-4'></IC_AUDIO_FILE>
                        <div className='flex-1 flex flex-col items-start overflow-hidden'>
                          <span className='w-full text-size-4 text-#0D062D truncate mr-3 text-align-start'>{file.file.name}</span>
                          <span className='mt-1 text-#A1A7B2 text-size-3'>{file.inProcess ? '正在处理中' : formatBytes(file.size)}</span>
                        </div>
                        <IC_CLOSE_ITEM className='w-5 h-5 cursor-pointer' onClick={() => {
                          handleDeleteFile(index, file.uid);
                        }}></IC_CLOSE_ITEM>
                      </div>
                    );
                  })}
                </div>
              </div> :
              <div className='w-full h-75 flex flex-col justify-center items-center rounded-2 border-dashed border-1 border-#DBDEE4'>
                <IC_ADD className='w-10 h-10 mb-4'></IC_ADD>
                <span className='text-#30343C text-size-4 lh-6'>将文件拖拽到此区域或<span className='text-#4981F2'>选择文件上传</span></span>
                <span className='mt-2 text-size-3 text-#A1A7B2 lh-6'>音频格式：mp3/wav/m4a，单个文件不超过200M</span>
                <span className='text-size-3 text-#A1A7B2 lh-6'>文档大小：单个文件时长不超过3小时，每日最多上传10个文件</span>
              </div>
          }
        </Dragger>

        <div className='h-14 px-6 w-full flex justify-end items-center border-t-#ECEEF3 border-t-1 border-t-solid'>
          <div className='w-22 h-8 text-mx-secondary rd-1.5 px-4 flex flex-items-center flex-justify-center border-1 border-#ECEEF3 border-solid cursor-pointer text-size-3.5'
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >取消</div>
          <button
            className={`w-22 ${canClick ? 'bg-mx-primary hover:bg-#4981F2E6' : 'bg-#C7DFFF'} h-8 rd-1.5 px-4 ml-4 flex flex-items-center flex-justify-center text-white text-size-3.5  outline-none border-none`}
            disabled={!canClick}
            onClick={(e) => {
              e.stopPropagation();
              handleStart();
            }}
          >
            开始转写
          </button>
        </div>
      </div>
    </div >,
    document.body
  );
}
